import type {
  FloorPlannerCamera,
  FloorPlannerLayout,
  FloorPlannerObserver,
  FloorPlannerObserverReference,
  ObserverTrackSide,
} from '@repo/frontend-types';
import {
  asArray,
  findLatestDesign,
  floorTargetRefid,
  isLive,
  layoutCurrentDesigns,
} from '@repo/front-end-utils';

// Type-only: erased at build, so it pulls none of the `@repo/ui` barrel into this module's tests.
import type { FrameRegion } from '@repo/ui';

import type { Polygon } from '../../components/PolygonPlotter2/PolygonPlotter2';

/**
 * The Observers stage's editing session, as data rather than as React state.
 *
 * Extracted from `OnboardingObservers.tsx` so the two halves of the round trip — hydrating a saved
 * plan into the session, and building the plan's observer records back out of it — can be tested
 * without rendering a component that pulls the planner canvas and the whole `@repo/ui`
 * barrel into jsdom. Nothing about the behaviour changed in the move; what it does today is pinned
 * by `observerSessionState.test.ts`, which was written against the extracted functions before any
 * of it was altered (`docs/refactoring-charter.md` §4).
 */

/**
 * What a STORED observer with no track side reads as, and files as.
 *
 * ⚠ **A fallback, not the default for a new region.** Changing it would re-mean every stored
 * observer that has none, so it stays `TORSO`. A region drawn or linked in this session takes
 * `NEW_OBSERVER_TRACK_SIDE` instead (`observers-page-camera-stage`, FR10).
 */
export const DEFAULT_TRACK_SIDE: ObserverTrackSide = 'TORSO';

/** The body part a region drawn or linked in this session tracks until the operator picks another (Rob, 2026-10-01). */
export const NEW_OBSERVER_TRACK_SIDE: ObserverTrackSide = 'FEET';

/**
 * The six keypoints this app can represent — ONE declaration, three readers.
 *
 * The editor offers these, the matcher checks a loaded value against them, and the notices name an
 * unrecognised one. All three were separate lists, so adding a seventh keypoint meant finding three
 * places and a miss meant the matcher reporting a value the editor was happily offering.
 */
export const TRACK_SIDE_OPTIONS: readonly ObserverTrackSide[] = [
  'WHOLE_BODY',
  'HEAD',
  'TORSO',
  'HIPS',
  'LEGS',
  'FEET',
];

/** `WHOLE_BODY` → `Whole body`. One spelling of the rule, for every surface that shows one. */
export const trackSideLabel = (side: string): string =>
  side
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/^./, c => c.toUpperCase());

/**
 * What the observer editor's hover tip says about one observer: its name and the body part it
 * tracks (Rob, 2026-09-24).
 *
 * **The name is the one Go Live will file it under, and never a guess.** The server names an
 * observer `{entity name}` or `{entity name}.{N}`, taking N from the stored camera number where the
 * observer already has one and ALLOCATING the lowest free number where it does not
 * (`statement-file-plan.ts`). The first is a fact the browser holds; the second is the server's
 * decision, made at filing, against numbers this session may not see. So:
 *
 * - a stored number is shown — `Treadmill 3.2`;
 * - an observer DRAWN this session with no number, on an entity that has others, reads
 *   `Treadmill 3 · new` — it will be numbered, and saying which number would be a claim about a
 *   decision not yet made;
 * - anything else with no number known reads `Treadmill 3`. That includes a LOADED observer
 *   (`loaded`: its refid carries the live marker) whose number the browser cannot see — the
 *   statement still loading or failed, or a camera stored bare. It already exists, so calling it
 *   "new" would be false; its bare name claims nothing it does not know.
 *
 * `entityName` is `observerTargetNamesByRefid`'s — the same name Go Live sends as `entityNames` —
 * falling back to `fallbackName` (the entity's BID, the server's own fallback) where the plan has
 * none.
 *
 * **The body part is the one that files**: an absent track side files as `DEFAULT_TRACK_SIDE`
 * (`buildObserverCfg`), so it reads as that, never as "not set".
 */
export const observerTip = ({
  entityName,
  fallbackName,
  loaded,
  storedOrdinal,
  siblingCount,
  trackSide,
}: {
  entityName: string | undefined;
  fallbackName: string;
  /** Loaded from the live configuration (its refid carries the live marker), not drawn here. */
  loaded: boolean;
  /** The stored camera number, from the live statement; `undefined` where there is none. */
  storedOrdinal: number | undefined;
  /** Complete observers on the same entity in this session, this one included. */
  siblingCount: number;
  trackSide: ObserverTrackSide | undefined;
}): ObserverTip => {
  const name = entityName?.trim() || fallbackName;
  const isNew = storedOrdinal === undefined && !loaded && siblingCount > 1;
  const title =
    storedOrdinal !== undefined ? `${name}.${storedOrdinal}` : isNew ? `${name} \u00b7 new` : name;
  return {
    title,
    detail: trackSideLabel(trackSide ?? DEFAULT_TRACK_SIDE),
    ordinal: storedOrdinal,
    isNew,
  };
};

/**
 * What `observerTip` knows about one observer: the two lines of text, and the two FACTS behind the
 * first — the stored camera number it shows, and whether it is a region drawn this session that the
 * server will number at filing. Everything that orders observers or marks a region reads the facts,
 * never the text: an entity whose own name ends in `.5` would otherwise read as filed number 5.
 */
export interface ObserverTip {
  title: string;
  detail: string;
  ordinal: number | undefined;
  isNew: boolean;
}

/** The mark drawn at a region's centre: its stored number, `new`, or nothing. */
export const regionMarker = (tip: Pick<ObserverTip, 'ordinal' | 'isNew'>): string =>
  tip.ordinal !== undefined ? String(tip.ordinal) : tip.isNew ? 'new' : '';

/**
 * Observers in FILED order: by stored camera number, the unnumbered after them in the order given.
 * One comparator for every surface that lists them — the overview's tiles and captions and the
 * observer strip — so they cannot disagree about which comes first.
 */
export const byFiledOrder = <T extends { tip: Pick<ObserverTip, 'ordinal'> }>(
  items: readonly T[],
): T[] =>
  items
    .map((item, drawn) => ({ item, drawn, n: item.tip.ordinal ?? Number.POSITIVE_INFINITY }))
    .sort((a, b) => (a.n === b.n ? a.drawn - b.drawn : a.n - b.n))
    .map(({ item }) => item);

/**
 * Where each entity lives, and which cameras its floor carries.
 *
 * The canvas and inventory panel show one floor at a time, but editing state is keyed by the
 * globally-unique entity refid, so saving and hydrating span floors: this resolves which floor each
 * drawn observer belongs to, and that floor's cameras for the RTSP-bid link.
 */
export type FloorEntityIndex = Map<
  string,
  { kind: 'surface' | 'item' | 'floor'; floorIndex: number; cameras: FloorPlannerCamera[] }
>;

/**
 * Per-observer editing state, keyed by the observer's own refid — `{targetRefid}:{polygonId}`, the
 * same string `buildObserversByFloor` mints — rather than by the entity.
 *
 * ⚠ **This is the shape that replaced `sourceByEntity` / `trackSideByEntity`**, and the reason is
 * the domain rather than tidiness: an observer watches one camera, and an entity can be watched by
 * several observers on several cameras (Rob, 2026-09-08). Measured on AdminDB Production, 106
 * entities across 10 twins are watched through more than one camera and 23 carry observers that
 * disagree on track side — none of which the per-entity maps could hold, so loading a live
 * configuration silently collapsed them onto the first observer's values.
 */
/**
 * An entity's geometry at a moment in time — what an observer was drawn against
 * (`camera-observer-setup-attention`, spec FR3). Held in `observerStamps.ts`, keyed by observer
 * refid; never on the observer record (`buildObserversByFloor` writes its record from named
 * fields, pinned by test). Saved on the plan as `observerReferenceGeometry`
 * (`onboarding-polish-observer-notices`), which is why it is that type.
 */
export type EntityGeometry = FloorPlannerObserverReference;

export interface ObserverEditState {
  endpointId: number | null;
  trackSide: ObserverTrackSide;
}

/** The entity's current geometry, in the shape `drawnAgainst` holds. `null` when it is not on the plan. */
export const geometryOf = (
  layout: FloorPlannerLayout | null | undefined,
  refid: string,
): EntityGeometry | null => {
  for (const design of layoutCurrentDesigns(layout ?? null)) {
    const item = asArray(design?.items).find(i => isLive(i) && i.refid === refid);
    if (item) return { kind: 'item', x: item.x, y: item.y, rotation: item.rotation };
    const surface = asArray(design?.surfaces).find(s => isLive(s) && s.refid === refid);
    if (surface) {
      return { kind: 'surface', poly: asArray(surface.poly).map(p => ({ x: p.x, y: p.y })) };
    }
  }
  return null;
};

export type ObserverStateByRefid = Record<string, ObserverEditState>;

/** The observer refid minted for a polygon drawn against an entity. One definition, three readers. */
export const observerRefid = (targetRefid: string, polygonId: string): string =>
  `${targetRefid}:${polygonId}`;

/**
 * Recover the polygon id from a saved observer's refid.
 *
 * ⚠ **This is a fix, not a formatting choice, and without it the refid GROWS on every save.**
 * Hydration used to store the whole saved refid as the polygon id, and `buildObserversByFloor` then
 * re-prefixed it — so an observer saved as `X:p1` came back as `X:X:p1`, then `X:X:X:p1`, gaining a
 * segment every round trip. That was cosmetic while nothing keyed on the refid; it is not, now that
 * `ObserverStateByRefid` does, because a key that changes on every save loses the camera and track
 * side it was holding. Pinned by `observerSessionState.test.ts`.
 */
export const polygonIdFromObserverRefid = (refid: string, targetRefid: string): string => {
  const prefix = `${targetRefid}:`;
  return refid.startsWith(prefix) ? refid.slice(prefix.length) : refid;
};

/** Index every entity across every floor, with its floor's cameras. */
export const buildFloorEntityIndex = (layout: FloorPlannerLayout | null): FloorEntityIndex => {
  const map: FloorEntityIndex = new Map();
  if (!layout) return map;
  asArray(layout.floors).forEach((floor, floorIndex) => {
    const design = findLatestDesign(asArray(floor?.designs));
    const cameras = asArray(design?.cameras).filter(isLive);
    asArray(design?.surfaces)
      .filter(isLive)
      .forEach(s => map.set(s.refid, { kind: 'surface', floorIndex, cameras }));
    asArray(design?.items)
      .filter(isLive)
      .forEach(i => map.set(i.refid, { kind: 'item', floorIndex, cameras }));
    /*
     * The floor itself, as a target a camera region can count (`refiling-preserves-configuration`).
     * Keyed by its synthetic refid, which no surface or item refid can equal, so saving and
     * hydrating a floor-wide observer go through exactly the same path as a zone's.
     */
    if (typeof floor?.id === 'number') {
      map.set(floorTargetRefid(floor.id), { kind: 'floor', floorIndex, cameras });
    }
  });
  return map;
};

/**
 * Flatten the drawn observers into the plan's persisted shape, grouped by the floor each target
 * entity lives on (index = floor index).
 *
 * One record per completed polygon, carrying its target entity and **its own** camera feed and
 * track side. The frame image is not persisted — it is re-captured from the feed at activation,
 * where the live observer records are created.
 *
 * ⚠ There is deliberately NO `name` on the record (`plan-item-register-sync`, epic ledger row 61).
 * It used to snapshot the target's name at DRAW time and Go Live sent that string to dGraph, so
 * renaming the target in between shipped an observer named after an asset that no longer had that
 * name. The name is derived from `targetRefid` at activation instead.
 */
export const buildObserversByFloor = (
  polygonsByEntity: Record<string, Polygon[]>,
  observerState: ObserverStateByRefid,
  floorEntityIndex: FloorEntityIndex,
  floorCount: number,
): FloorPlannerObserver[][] => {
  const byFloor: FloorPlannerObserver[][] = Array.from({ length: floorCount }, () => []);

  for (const [refid, polys] of Object.entries(polygonsByEntity)) {
    const complete = polys.filter(p => p.isComplete);
    if (complete.length === 0) continue;
    const meta = floorEntityIndex.get(refid);
    if (!meta) continue; // entity no longer on the plan

    for (const p of complete) {
      const id = observerRefid(refid, p.id);
      const endpointId = observerState[id]?.endpointId ?? null;
      const rtspEndpointBid =
        endpointId != null
          ? (meta.cameras.find(c => c.rtspEndpointId === endpointId)?.rtspEndpointBid ?? null)
          : null;
      byFloor[meta.floorIndex].push({
        refid: id,
        targetRefid: refid,
        targetKind: meta.kind,
        rtspEndpointId: endpointId,
        rtspEndpointBid,
        points: p.points,
        trackSide: observerState[id]?.trackSide ?? DEFAULT_TRACK_SIDE,
      });
    }
  }

  return byFloor;
};

/**
 * Hydrate the editing session from every floor's saved observers.
 *
 * State is keyed by globally-unique refids, so a single merge across floors is correct. The frame
 * image is not stored, so only the polygon, the camera link and the track side are restored; the
 * frame re-loads when the entity is opened.
 */
export const hydrateObserverSession = (
  layout: FloorPlannerLayout | null,
): { polygonsByEntity: Record<string, Polygon[]>; observerState: ObserverStateByRefid } => {
  const polygonsByEntity: Record<string, Polygon[]> = {};
  const observerState: ObserverStateByRefid = {};

  const saved = asArray(layout?.floors)
    .flatMap(f => asArray(findLatestDesign(asArray(f?.designs))?.observers))
    .filter(isLive);

  for (const o of saved) {
    const polygonId = polygonIdFromObserverRefid(o.refid, o.targetRefid);
    (polygonsByEntity[o.targetRefid] ??= []).push({
      id: polygonId,
      points: o.points,
      isComplete: true,
    });
    observerState[observerRefid(o.targetRefid, polygonId)] = {
      endpointId: o.rtspEndpointId ?? null,
      trackSide: o.trackSide ?? DEFAULT_TRACK_SIDE,
    };
  }

  return { polygonsByEntity, observerState };
};

/**
 * The open entity's observers, grouped by the camera each sits on — the one place the rule "a
 * camera appears once, however many of the entity's observers it carries" is written
 * (`multi-camera-observer-view`, R1).
 *
 * Complete polygons only: a half-drawn region is not yet an observer. Groups are in the order their
 * camera first appears in the entity's draw order, and the observers inside a group keep draw order.
 * An observer with no camera (`endpointId` absent or `null` — measured on AdminDB Staging, where
 * loaded configurations carry them) forms its own group, placed LAST so every picture comes first.
 */
export interface ObserverCameraGroup {
  endpointId: number | null;
  observers: Array<{ refid: string; polygon: Polygon }>;
}

export const observerCameraGroups = (
  entityRefid: string,
  polygons: readonly Polygon[],
  observerState: ObserverStateByRefid,
): ObserverCameraGroup[] => {
  const byCamera = new Map<number | null, ObserverCameraGroup>();
  for (const polygon of polygons) {
    if (!polygon.isComplete) continue;
    const refid = observerRefid(entityRefid, polygon.id);
    const endpointId = observerState[refid]?.endpointId ?? null;
    let group = byCamera.get(endpointId);
    if (!group) {
      group = { endpointId, observers: [] };
      byCamera.set(endpointId, group);
    }
    group.observers.push({ refid, polygon });
  }
  const groups = Array.from(byCamera.values());
  return [
    ...groups.filter(g => g.endpointId !== null),
    ...groups.filter(g => g.endpointId === null),
  ];
};

/**
 * Whether one of the open entity's polygons belongs to the drawing area showing `endpointId`.
 *
 * The single ownership rule the per-camera seed and the merge both read, so the two cannot disagree
 * about which regions the plotter holds. A polygon that is not complete, or has no state entry yet,
 * exists only in the plotter drawing it — it belongs to the camera on screen.
 */
export const isOnCamera = (
  entityRefid: string,
  polygon: Polygon,
  observerState: ObserverStateByRefid,
  endpointId: number | null,
): boolean => {
  if (!polygon.isComplete) return true;
  const state = observerState[observerRefid(entityRefid, polygon.id)];
  if (!state) return true;
  return (state.endpointId ?? null) === endpointId;
};

/** The open entity's polygons that the drawing area on `endpointId` shows and edits. */
export const polygonsOnCamera = (
  entityRefid: string,
  polygons: readonly Polygon[],
  observerState: ObserverStateByRefid,
  endpointId: number | null,
): Polygon[] => polygons.filter(p => isOnCamera(entityRefid, p, observerState, endpointId));

/**
 * The open entity's whole polygon list after the drawing area on ONE camera reports its set.
 *
 * ⚠ The plotter only ever holds the shown camera's regions, and reports its whole set on every
 * edit. Writing that set straight over the entity — what the editor did while the plotter held
 * every region — would delete every other camera's region on the first edit. So:
 *
 * - an off-camera polygon is kept, by reference and in place;
 * - an on-camera polygon takes the plotter's version, or is dropped when the plotter no longer has it;
 * - anything the plotter holds that the entity did not (a new region) is appended.
 */
export const mergeCameraPolygons = (
  all: readonly Polygon[],
  edited: readonly Polygon[],
  isOffCamera: (polygon: Polygon) => boolean,
): Polygon[] => {
  const editedById = new Map(edited.map(p => [p.id, p]));
  const emitted = new Set<string>();
  const result: Polygon[] = [];
  for (const polygon of all) {
    if (isOffCamera(polygon)) {
      result.push(polygon);
      continue;
    }
    const next = editedById.get(polygon.id);
    if (next) {
      result.push(next);
      emitted.add(next.id);
    }
  }
  for (const polygon of edited) {
    if (!emitted.has(polygon.id)) result.push(polygon);
  }
  return result;
};

/**
 * Link a region drawn with nothing picked to the entity picked next (the camera workspace, spec R7).
 *
 * The region becomes a NEW observer of `targetRefid` on `endpointId`, exactly the shape a region
 * drawn with the entity already picked takes in `handlePolygonChange`: appended to the entity's
 * list, completed, its points untouched, and given a `{ endpointId, DEFAULT_TRACK_SIDE }` entry
 * under its observer refid. So filing cannot tell a linked region from a drawn one — which is the
 * point: an entity with regions elsewhere simply becomes a partial group.
 *
 * The polygon id is kept unless the entity already uses it (or its observer refid already has
 * state), in which case a suffix is added until it is free. Deterministic, never a timestamp: two
 * links in one millisecond must not collide.

 *
 * Pure — the caller commits both results and stamps the returned `observerRefid`.
 */
export const linkRegion = (
  polygonsByEntity: Record<string, Polygon[]>,
  observerState: ObserverStateByRefid,
  targetRefid: string,
  region: Polygon,
  endpointId: number,
  trackSide: ObserverTrackSide = NEW_OBSERVER_TRACK_SIDE,
): {
  polygonsByEntity: Record<string, Polygon[]>;
  observerState: ObserverStateByRefid;
  observerRefid: string;
} => {
  const existing = polygonsByEntity[targetRefid] ?? [];
  const taken = (id: string) =>
    existing.some(p => p.id === id) || observerRefid(targetRefid, id) in observerState;
  let id = region.id;
  for (let n = 2; taken(id); n += 1) id = `${region.id}_${n}`;
  const linked: Polygon = { ...region, id, isComplete: true };
  const refid = observerRefid(targetRefid, id);
  return {
    polygonsByEntity: { ...polygonsByEntity, [targetRefid]: [...existing, linked] },
    observerState: { ...observerState, [refid]: { endpointId, trackSide } },
    observerRefid: refid,
  };
};

/**
 * The camera a region is on, or `null` when it is on none: a half-drawn region is not an observer,
 * and an observer whose state carries no `endpointId` is on no camera.
 *
 * The one answer to "which feed is this region on" — the backdrop, the hover zoom, the Cameras
 * view's per-feed count and the camera workspace's per-entity count all ask it, and each had its
 * own copy of these two lines.
 */
export const endpointOfRegion = (
  targetRefid: string,
  p: Polygon,
  observerState: ObserverStateByRefid,
): number | null => {
  if (!p.isComplete) return null;
  return observerState[observerRefid(targetRefid, p.id)]?.endpointId ?? null;
};

/**
 * Each entity's complete regions on ONE camera (`workspace-inventory-this-camera`, R1/R4): what
 * the camera workspace lists beyond the cone, and the "{n} on this camera" on its rows. Only
 * entities with a region there get a key.
 */
export const regionsOnFeed = (
  polygonsByEntity: Record<string, Polygon[]>,
  observerState: ObserverStateByRefid,
  endpointId: number,
): Record<string, number> => {
  const counts: Record<string, number> = {};
  for (const [refid, polys] of Object.entries(polygonsByEntity)) {
    for (const p of polys) {
      if (endpointOfRegion(refid, p, observerState) !== endpointId) continue;
      counts[refid] = (counts[refid] ?? 0) + 1;
    }
  }
  return counts;
};

/**
 * The read-only backdrop for the drawing area on `endpointId`: every completed region on that camera
 * that belongs to ANOTHER entity. Lets the operator place a region relative to what is already set up
 * on the same picture.
 *
 * ⚠ Keyed on the CAMERA SHOWN, not on the selected observer, and excluding the whole OPEN ENTITY.
 * Keyed on the selected observer, a first region drawn on a just-picked camera had no backdrop at all
 * (there is no observer yet); and excluding only the selected observer drew a same-camera sibling of
 * the open entity twice — once as its own editable region and once dashed behind it.
 *
 * Derived purely from local state — no refetch on switch. Polygon ids are namespaced by their source
 * entity so the ids stay unique across entities (the plotter keys frame polygons by id).
 */
export const backdropPolygonsOnCamera = (
  endpointId: number | null | undefined,
  openEntityRefid: string | null | undefined,
  polygonsByEntity: Record<string, Polygon[]>,
  observerState: ObserverStateByRefid,
): Polygon[] => {
  if (endpointId == null) return [];

  const result: Polygon[] = [];
  for (const [refid, polys] of Object.entries(polygonsByEntity)) {
    if (refid === openEntityRefid) continue;
    for (const p of polys) {
      if (endpointOfRegion(refid, p, observerState) !== endpointId) continue;
      result.push({ ...p, id: observerRefid(refid, p.id) });
    }
  }
  return result;
};

/**
 * Every camera's observer regions, for the hover zoom (`camera-zoom-dwell-and-observers`, FR8): a
 * map from endpoint id to that camera's regions in the zoom's `outline` style.
 *
 * The one builder both stages call — the Camera Feeds page on the plan's saved observers
 * (`hydrateObserverSession`), the Observers page on its live session — so "which regions does this
 * camera's zoom show" has one answer. Complete polygons only (a half-drawn region is not an
 * observer), and an observer with no camera is on no camera's zoom.
 *
 * ⚠ **`previous` is what keeps the camera wall's memo working.** `CameraWallTile` compares its
 * `zoomRegions` by identity, and `polygonsByEntity` changes on every pointer move of a drag. So a
 * camera whose regions are the same polygons as last time — the same `points` arrays, which
 * `mergeCameraPolygons` keeps by reference for every camera but the one being edited — gets its
 * PREVIOUS array back, and only the edited camera's tile re-renders.
 */
export const zoomRegionsByCamera = (
  polygonsByEntity: Record<string, Polygon[]>,
  observerState: ObserverStateByRefid,
  previous?: ReadonlyMap<number, readonly FrameRegion[]>,
): Map<number, readonly FrameRegion[]> => {
  const built = new Map<number, FrameRegion[]>();
  for (const [targetRefid, polys] of Object.entries(polygonsByEntity)) {
    for (const p of polys) {
      const endpointId = endpointOfRegion(targetRefid, p, observerState);
      if (endpointId == null) continue;
      const refid = observerRefid(targetRefid, p.id);
      let list = built.get(endpointId);
      if (!list) {
        list = [];
        built.set(endpointId, list);
      }
      list.push({ id: refid, points: p.points, style: 'outline' });
    }
  }

  const result = new Map<number, readonly FrameRegion[]>();
  built.forEach((list, endpointId) => {
    const prev = previous?.get(endpointId);
    const same =
      !!prev &&
      prev.length === list.length &&
      prev.every((r, i) => r.id === list[i].id && r.points === list[i].points);
    result.set(endpointId, same ? prev : list);
  });
  return result;
};

/** The editing session's observer data — what one undo entry restores (`observers-page-camera-stage`, FR16). */
export interface ObserverSessionSnapshot {
  polygonsByEntity: Record<string, Polygon[]>;
  observerState: ObserverStateByRefid;
}

/**
 * A bounded last-in-first-out history: the page's one undo (`observers-page-camera-stage`, FR16).
 * Pushing past `cap` drops the oldest entry. `peek` lets a toast ask whether its entry is newest.
 */
export interface UndoStack<T> {
  push: (entry: T) => void;
  pop: () => T | undefined;
  peek: () => T | undefined;
  clear: () => void;
  size: () => number;
}

export const createUndoStack = <T>(cap = 50): UndoStack<T> => {
  let entries: T[] = [];
  return {
    push: entry => {
      entries = [...entries, entry].slice(-cap);
    },
    pop: () => {
      const last = entries[entries.length - 1];
      entries = entries.slice(0, -1);
      return last;
    },
    peek: () => entries[entries.length - 1],
    clear: () => {
      entries = [];
    },
    size: () => entries.length,
  };
};
